// Pure computation behind useQuickFillStore.autoFillRowsWithLabels.
// Kept store-agnostic so the row-update/diagnostics logic can be tested and
// reasoned about without zustand; the store method is a thin get/set adapter.
import type { QuickFillItem } from '../../types';
import {
  autoFillFromQuickFillItem,
  canReplaceWeakAutoFillPlaceholder,
  isModelTextAutoFillResult,
  isSkeyAutoFillColumn,
  resolveAutoFillValueForColumn,
  resolveQuickFillAutoFillSourceName,
  type AutoFillResult,
  type QuickFillAutoFillMode,
} from '../utils/autoFillService';

export interface AutoFillRowDiagnostics {
  missingItemRows: number;
  noConfidenceRows: number;
  noEditableLabelRows: number;
  missingAutoFillValueCells: number;
  noTouchedTargetCells: number;
  touchedTargetCells: number;
  readOnlyLabels: Record<string, number>;
  missingValueLabels: Record<string, number>;
  noTouchedLabels: Record<string, number>;
  samples: unknown[];
}

export interface AutoFillRowComputationResult {
  updatedData: QuickFillItem[];
  filledCount: number;
  diagnostics: AutoFillRowDiagnostics;
  results: Map<number, AutoFillResult>;
}

export const AutoFillLogSampleSize = 8;

export const getCellProtection = (item: QuickFillItem | undefined, field: string): { readOnly: boolean; hasParam: boolean } => {
  const readonlyFlags = ((item as any)?.__readonlyFlags as Record<string, boolean> | undefined) || {};
  const hasParamFlags = ((item as any)?.__hasParamFlags as Record<string, boolean> | undefined) || {};
  return {
    readOnly: readonlyFlags[field] === true,
    hasParam: hasParamFlags[field] !== false,
  };
};

const getBindingScope = (item: QuickFillItem | undefined, field: string): string => {
  const bindingScopeFlags =
    (((item as any)?.__bindingScopeFlags as Record<string, string> | undefined) || {});
  return bindingScopeFlags[field] ?? 'unknown';
};

const getFamilyTypeKey = (item: QuickFillItem | undefined): string => {
  const rawValue = (item as any)?.__familyTypeKey;
  return typeof rawValue === 'string' && rawValue.trim() !== ''
    ? rawValue.trim()
    : item?.Name || '';
};

export const summarizeAutoFillStoreRow = (item: QuickFillItem | undefined, rowIndex: number) => {
  const record = item as Record<string, unknown> | undefined;
  return {
    rowIndex,
    source: resolveQuickFillAutoFillSourceName(record),
    name: typeof record?.Name === 'string' ? record.Name : '',
    rowMode: record?.__rowMode,
    snapshotShape: record?.__quickFillSnapshotShape,
    familyName: record?.__familyName,
    typeName: record?.__typeName,
    familyTypeKey: record?.__familyTypeKey,
    elementId: record?.__elementId,
  };
};

const incrementCounter = (counters: Record<string, number>, key: string): void => {
  counters[key] = (counters[key] || 0) + 1;
};

const getAutoFillTargetRowIndices = (
  data: QuickFillItem[],
  rowIndex: number,
  field: string,
  familyTypeIndex: ReadonlyMap<string, readonly number[]>
): number[] => {
  const row = data[rowIndex];
  if ((row as any)?.__rowMode !== 'instance' || getBindingScope(row, field) !== 'type') {
    return [rowIndex];
  }

  const familyTypeKey = getFamilyTypeKey(row);
  if (!familyTypeKey) {
    return [rowIndex];
  }

  return (familyTypeIndex.get(familyTypeKey) ?? []).filter(
    (candidateIndex) => getBindingScope(data[candidateIndex], field) === 'type'
  );
};

const buildFamilyTypeRowIndex = (
  data: QuickFillItem[]
): ReadonlyMap<string, readonly number[]> => {
  const index = new Map<string, number[]>();
  data.forEach((item, rowIndex) => {
    const familyTypeKey = getFamilyTypeKey(item);
    if (!familyTypeKey) return;
    const rows = index.get(familyTypeKey) ?? [];
    rows.push(rowIndex);
    index.set(familyTypeKey, rows);
  });
  return index;
};

const resolveAutoFillValueForLabel = (
  result: AutoFillResult,
  label: string
): string | undefined => {
  return resolveAutoFillValueForColumn(result, label);
};

const shouldApplyAutoFillValue = (
  field: string,
  currentValue: unknown,
  nextValue: string,
  mode: QuickFillAutoFillMode
): boolean => {
  if (mode === 'overwrite') {
    return currentValue !== nextValue;
  }

  if (currentValue === undefined || currentValue === null || currentValue === '') {
    return true;
  }

  return (
    canReplaceWeakAutoFillPlaceholder(field, currentValue, nextValue)
  );
};

const applyAutoFillValueToRows = (
  data: QuickFillItem[],
  rowIndices: number[],
  field: string,
  value: string,
  mode: QuickFillAutoFillMode,
  clonedRowIndices: Set<number>
): number[] => {
  const touchedRowIndices = new Set<number>();

  rowIndices.forEach((rowIndex) => {
    const item = data[rowIndex];
    if (!item) {
      return;
    }

    const protection = getCellProtection(item, field);
    if (protection.readOnly) {
      return;
    }

    const currentValue = item[field as keyof QuickFillItem];
    const shouldUpdate = shouldApplyAutoFillValue(field, currentValue, value, mode);

    if (!shouldUpdate) {
      return;
    }

    if (!clonedRowIndices.has(rowIndex)) {
      data[rowIndex] = { ...item };
      clonedRowIndices.add(rowIndex);
    }
    (data[rowIndex] as Record<string, unknown>)[field] = value;
    touchedRowIndices.add(rowIndex);
  });
  return Array.from(touchedRowIndices).sort((left, right) => left - right);
};

export const sortUniqueRowIndices = (indices: number[]): number[] =>
  Array.from(new Set(indices.filter((index) => Number.isInteger(index) && index >= 0))).sort(
    (left, right) => left - right
  );

export const computeAutoFillRows = (
  data: QuickFillItem[],
  rowIndices: number[],
  allowedLabels: ReadonlySet<string>,
  mode: QuickFillAutoFillMode
): AutoFillRowComputationResult => {
  const results = new Map<number, AutoFillResult>();
  let filled = 0;
  const updatedData = [...data];
  const clonedRowIndices = new Set<number>();
  const familyTypeIndex = buildFamilyTypeRowIndex(data);
  const autoFillResultBySource = new Map<string, AutoFillResult>();
  const appliedTypeRoutes = new Set<string>();
  const editableLabels = Array.from(allowedLabels).filter((label) => label !== 'Name');
  const diagnostics: AutoFillRowDiagnostics = {
    missingItemRows: 0,
    noConfidenceRows: 0,
    noEditableLabelRows: 0,
    missingAutoFillValueCells: 0,
    noTouchedTargetCells: 0,
    touchedTargetCells: 0,
    readOnlyLabels: {} as Record<string, number>,
    missingValueLabels: {} as Record<string, number>,
    noTouchedLabels: {} as Record<string, number>,
    samples: [] as unknown[],
  };

  const addSample = (sample: Record<string, unknown>): void => {
    if (diagnostics.samples.length < AutoFillLogSampleSize) {
      diagnostics.samples.push(sample);
    }
  };

  rowIndices.forEach((rowIndex) => {
    const item = updatedData[rowIndex];
    if (!item) {
      diagnostics.missingItemRows++;
      addSample({ rowIndex, reason: 'missing-item' });
      return;
    }

    const record = item as unknown as Record<string, unknown>;
    const resultKey = `${resolveQuickFillAutoFillSourceName(record)}::${String(record.__category ?? '')}`;
    let result = autoFillResultBySource.get(resultKey);
    if (!result) {
      result = autoFillFromQuickFillItem(record);
      autoFillResultBySource.set(resultKey, result);
    }
    if (isModelTextAutoFillResult(result)) {
      // Writes for Model Text rows always fail (not a loadable family).
      diagnostics.noConfidenceRows++;
      addSample({
        ...summarizeAutoFillStoreRow(item, rowIndex),
        reason: 'model-text-unwritable',
      });
      return;
    }

    results.set(rowIndex, result);

    if (result.confidence <= 0) {
      diagnostics.noConfidenceRows++;
      addSample({
        ...summarizeAutoFillStoreRow(item, rowIndex),
        reason: 'no-confidence',
        confidence: result.confidence,
        matchedPatternId: result.matchedPatternId,
        matchedKeywords: result.matchedKeywords,
      });
      return;
    }

    const familyTypeKey = getFamilyTypeKey(item);
    if (
      (item as any)?.__rowMode === 'instance' &&
      editableLabels.length > 0 &&
      editableLabels.every((label) =>
        getBindingScope(item, label) === 'type' &&
        appliedTypeRoutes.has(`${familyTypeKey}::${label}`)
      )
    ) {
      return;
    }

    const readOnlySkippedLabels: string[] = [];
    const editableCheckedLabels = editableLabels.filter((label) => {
      const protection = getCellProtection(item, label);
      if (protection.readOnly) {
        incrementCounter(diagnostics.readOnlyLabels, label);
        readOnlySkippedLabels.push(label);
        return false;
      }
      return true;
    });
    if (editableCheckedLabels.length === 0) {
      diagnostics.noEditableLabelRows++;
      addSample({
        ...summarizeAutoFillStoreRow(item, rowIndex),
        reason: 'no-editable-labels',
        confidence: result.confidence,
        result: {
          objectType: result.objectType,
          skey: result.skey,
          end1: result.end1,
          end2: result.end2,
          end3: result.end3,
          end4: result.end4,
        },
        allowedLabels: Array.from(allowedLabels),
        readOnlySkippedLabels,
      });
      return;
    }

    let rowChanged = false;
    const rowDiagnostics = diagnostics.samples.length < AutoFillLogSampleSize
      ? [] as Array<Record<string, unknown>>
      : null;
    editableCheckedLabels.forEach((label) => {
      const nextValue = resolveAutoFillValueForLabel(result, label);
      const isIgnoreSkeyClear = result.objectType === 'IGNORE' &&
        isSkeyAutoFillColumn(label) &&
        nextValue === '';
      if ((nextValue === undefined || nextValue === null || nextValue === '') && !isIgnoreSkeyClear) {
        diagnostics.missingAutoFillValueCells++;
        incrementCounter(diagnostics.missingValueLabels, label);
        rowDiagnostics?.push({ label, reason: 'no-autofill-value' });
        return;
      }

      const typeRouteKey = (item as any)?.__rowMode === 'instance' &&
        getBindingScope(item, label) === 'type'
        ? `${getFamilyTypeKey(item)}::${label}`
        : null;
      if (typeRouteKey && appliedTypeRoutes.has(typeRouteKey)) {
        diagnostics.noTouchedTargetCells++;
        incrementCounter(diagnostics.noTouchedLabels, label);
        rowDiagnostics?.push({ label, nextValue, reason: 'type-route-already-applied' });
        return;
      }
      if (typeRouteKey) appliedTypeRoutes.add(typeRouteKey);
      const targetRowIndices = getAutoFillTargetRowIndices(
        updatedData,
        rowIndex,
        label,
        familyTypeIndex
      );
      const touchedRowIndices = applyAutoFillValueToRows(
        updatedData,
        targetRowIndices,
        label,
        nextValue,
        mode,
        clonedRowIndices
      );
      if (touchedRowIndices.length > 0) {
        rowChanged = true;
        diagnostics.touchedTargetCells += touchedRowIndices.length;
        rowDiagnostics?.push({
          label,
          nextValue,
          targetRows: targetRowIndices.length,
          touchedRows: touchedRowIndices.length,
        });
      } else {
        diagnostics.noTouchedTargetCells++;
        incrementCounter(diagnostics.noTouchedLabels, label);
        rowDiagnostics?.push({
          label,
          nextValue,
          reason: 'no-targets-touched',
          targetRows: targetRowIndices.length,
          currentValue: item[label as keyof QuickFillItem],
        });
      }
    });

    if (rowChanged) {
      filled++;
    } else {
      addSample({
        ...summarizeAutoFillStoreRow(item, rowIndex),
        reason: 'matched-but-no-row-change',
        confidence: result.confidence,
        result: {
          objectType: result.objectType,
          skey: result.skey,
          end1: result.end1,
          end2: result.end2,
          end3: result.end3,
          end4: result.end4,
        },
        allowedLabels: Array.from(allowedLabels),
        editableCheckedLabels,
        rowDiagnostics: rowDiagnostics ?? [],
      });
    }
  });

  return { updatedData, filledCount: filled, diagnostics, results };
};
